import React, {useState, useEffect, useRef, useMemo} from 'react'
import {Tag, Tooltip, Button, Space, Popconfirm} from '@bedrock/components'
import {ShieldAlert, ChevronRight, Message, Close, Crown, LevelDown } from '@bedrock/icons-react'
import {useMemoizedFn} from 'ahooks'
import classNames from 'classnames/bind'
import styles from './index.less'
import {useSupplierRemarkModal} from "#features/SupplierRemarkModal";
import {SupplierRiskTagVo} from "#services/api";

const cx = classNames.bind(styles)

type RemarkTagProps = {
  supplier?: any,
  ableOpen?: boolean,
  showType?: 'tag' | 'link',
  showRemarkTag?: boolean,
  showRiskTag?: boolean,
} & React.HTMLAttributes<HTMLSpanElement>
const RemarkRiskTag = (props: RemarkTagProps) => {
  const {supplier, ableOpen = true} = props
  const openSupplierRemarkModal =useSupplierRemarkModal()
  const showAlter = useMemoizedFn((e: any) => {
    if (!ableOpen) return
    e.stopPropagation()
    // 可能是供应商名称的key;
    const nameKeys = ['name', 'supplier_name', 'main_supplier_name', 'supplier_full_name', 'label', 'full_name_with_service_info']
    openSupplierRemarkModal({
      record: {
        ...supplier,
        main_supplier_id: supplier?.main_supplier_id,
        main_supplier_name: nameKeys.map(key => supplier?.[key]).find(name => name) || '',
      },
      noteType: 'risk_remarks'
    })
  })

  if (!supplier?.risk_switch || !supplier?.risk_remark_count) {
    return null
  }

  if (props.showType === 'link') {
    return <Button
      className={cx('alter-tag-link')}
      icon={<Message />}
      size="xSmall"
      type="link"
      danger
      onClick={(e) => {
        showAlter(e)
      }}
    >
      风险{ableOpen && <>备注<ChevronRight  /></>}
      </Button>
  }

  return <Tag
    className={cx('alter-tag')}
    size="xSmall"
    color="light-red"
    text={<>风险{ableOpen && <>备注<ChevronRight  /></>}</>}
    icon={<ShieldAlert />}
    onClick={(e) => {
      showAlter(e)
    }}
  />
}

type AlterTagProps = {
  showRemarkTag?: boolean,
  showRiskTag?: boolean,
} & RemarkTagProps

const tagConfig = {
  1: {
    sort: 1,
    color: 'red',
  },
  9: {
    sort: 1.1,
    color: 'light-red',
  },
  2: {
    sort: 2,
    color: 'light-red',
  },
  3: {
    sort: 3,
    color: 'light-orange',
  },
  4: {
    sort: 4,
    color: 'light-blue',
  },
  LIMIT_COOPERATION: {
    sort: 1,
    color: 'red',
  },
  HIGH: {
    sort: 2,
    color: 'light-red',
  },
  MEDIUM: {
    sort: 3,
    color: 'light-orange',
  },
  LOW: {
    sort: 4,
    color: 'light-blue',
  },
  OTHER: {
    sort: 1.1,
    color: 'light-red',
  },
  default: {
    color: 'light-grey',
  }
}


// 风险标签列表
export function RiskTagList(props: {
  riskTags?: SupplierRiskTagVo[],
  maxShowLength?: number,
}) {
  const {riskTags: riskTagsProp, maxShowLength = 3} = props

  const riskTagDom = useMemo(() => {
    const riskTags = riskTagsProp?.sort?.((a, b) => {
      // 按照sort字段排序，没有sort字段的按照tag_level排序
      return (tagConfig[a.tag_level]?.sort || a.tag_level) - (tagConfig[b.tag_level]?.sort || b.tag_level)
    }) || [];
    if (!riskTags?.length) return null
    // 只显示前三个
    const riskTagList = riskTags.slice(0, maxShowLength).map((tag, index) => {
      const tagConfigItem = tagConfig[tag.tag_level] || tagConfig.default;
      return <Tooltip
        title={tag.desc}
        key={index}
        getPopupContainer={() => document.body}
      >
        <Tag
          key={index}
          size="xSmall"
          text={tag.label}
          {...tagConfigItem}
        />
      </Tooltip>
    })
    // 如果超过3个，则显示更多，在tooltip中展示
    if (riskTags.length > maxShowLength) {
      riskTagList.push(<Popconfirm
        noFooter
        trigger={'hover'}
        icon={''}
        type={'unset'}
        overlayClassName={cx('more-popconfirm')}
        content={<div>
          {riskTags.slice(maxShowLength).map((tag, index) => {
            const tagConfigItem = tagConfig[tag.tag_level] || tagConfig.default;
            return <div className={cx('tag-group')} key = {index}>
              <Tag
                className={cx('tag-item')}
                key={index}
                size="xSmall"
                text={tag.label}
                {...tagConfigItem}
              />
              <span
                className={cx('tag-desc-text')}
              >
                {tag.desc}
              </span>
            </div>
          })}
        </div>}
      >
        <Tag
          size="xSmall"
          text={`+${riskTags.length - maxShowLength}`}
          color="light-grey"
        />
      </Popconfirm>)
    }
    return riskTagList
  }, [riskTagsProp, maxShowLength])

  return <>{riskTagDom}</>
}


// 风险标签合并列表
export function AlertAllTagList(props: AlterTagProps) {
  const {supplier, showRemarkTag = true, showRiskTag = true, ...rest} = props

  const localShowRemarkTag = useMemo(() => {
    return showRemarkTag && supplier?.risk_switch && supplier?.risk_remark_count
  }, [showRemarkTag, supplier])

  const localShowRiskTag = useMemo(() => {
    return showRiskTag && !!supplier?.risk_tags?.length
  }, [showRiskTag, supplier])

  if (!localShowRemarkTag && !localShowRiskTag) {
    return null
  }

  return (!!localShowRemarkTag || !!localShowRiskTag) && <div className={cx('tag-list')} >
    {!!localShowRemarkTag && <RemarkRiskTag supplier={supplier} showType="tag" {...rest} />}
    {!!localShowRiskTag && <RiskTagList riskTags={supplier?.risk_tags || []} />}
  </div>
}

// 技术分待观察  标签
export function LowEfficiencyTag(props: React.ComponentProps<typeof Tag> & { supplier: any }) {
  const { supplier, ...restProps } = props
  if (!supplier?.is_low_efficiency) {
    return null
  }
  return <Tag {...restProps} text="技术分待观察" color="orange" size="xSmall" />
}


type SupplierNameProps = {
  wrapperLayout?: 'none' | 'inline-flex' | 'inline-block' | 'flex' | 'card',
  supplier: any,
  showSystags?: boolean,
  showRiskTags?: boolean,
  /** 展示渠道类型标签 */
  showChannelType?: boolean,
  /** 展示供应商等级标签 */
  showTypeTag?: boolean,
  alterTagProps?: AlterTagProps,
  onDelete?: (supplier: any, e: React.MouseEvent) => void,
  showIsExclusive?: boolean;
  showIsLowestPriceLast6m?: boolean;
  /** 是否展示警告样式 */
  warning?: boolean;
} & React.HTMLAttributes<HTMLSpanElement>

const selectionTypeConfig = {
  WINNING: { text: '中选', color: 'light-grey' as const },
  RESERVE: { text: '储备', color: 'light-red' as const },
  OTHER: { text: '其他', color: 'light-blue' as const },
  DEFAULT: { text: '非年框政策供应商', color: 'red' as const },
};

export default function SupplierName(props: SupplierNameProps) {
  const {supplier,
    className,
    onDelete,
    wrapperLayout = 'inline-block',
    alterTagProps,
    showSystags = true,
    showRiskTags = true,
    showChannelType = false,
    showTypeTag = false,
    showIsExclusive,
      showIsLowestPriceLast6m,
    warning,
    ...restProps} = props

  const { sys_tags, is_low_efficiency = false, segment_supplier_tag } = supplier || {}
  // 从 supplier.segment_supplier_tag 推导选择类型，为空或空对象则不展示
  const selectionType = segment_supplier_tag?.selection_type

  const SystagDom = useMemo(() => {
    if (!sys_tags?.length || !showSystags) return null
    return sys_tags.map((tag, index) => {
      return <Tooltip
          title={tag.desc}
          key={index}
      >
        <Tag
            key={index}
            border
            size="xSmall"
            text={tag.label}
            color="light-orange"
            className={cx('sys-tag')}
        />
      </Tooltip>
    })
  }, [sys_tags, showSystags])

  const selectionTag = selectionType ? (
    <Tag
      key="selectionTypeTag"
      size="xSmall"
      text={segment_supplier_tag?.selection_type_name}
      color={selectionTypeConfig[selectionType]?.color}
      className={cx('sys-tag', 'ml-0', 'mr-1')}
    />
  ) : null;

  const nameWithTags = [
    showChannelType && <Tag key="channelTypeTag" border size="xSmall" text={supplier?.channel_type_name} color="light-grey" className={cx('sys-tag')}/>,
    showTypeTag && <Tag key="supplierTypeTag" border size="xSmall" text={supplier?.type_desc} color="light-grey" className={cx('sys-tag')}/>,
    <LowEfficiencyTag key="lowEfficiencyTag" supplier={supplier} className={cx('sys-tag')} />,
    ...(SystagDom || []),
      showIsExclusive && <Tag
      text="独家资源"
      color="light-orange"
      border={true}
      size="xSmall"
      icon={<Crown />}
      className={cx('sys-tag')}
    />,
    showIsLowestPriceLast6m && <Tag
      text="6个月内最低价"
      color="light-green"
      border={true}
      size="xSmall"
      icon={<LevelDown />}
      className={cx('sys-tag')}
    />,
  ].filter(Boolean);

  const warningClassName = warning ? cx('supplier-name-warning') : undefined;

  if (wrapperLayout === 'none') {
    return <>
      {selectionTag}
      <span className={warningClassName} style={is_low_efficiency ? { color: 'var(--Text---text-color-3, #878A99)' } : undefined}>
        {props.children}
      </span>
      {nameWithTags}
      {showRiskTags && <AlertAllTagList {...alterTagProps} supplier={supplier}/> }
    </>
  }

  if (wrapperLayout === 'card') {
    return <div className={cx(
      'supplier-name', wrapperLayout,
      className, warningClassName)}
      {...restProps}>
      <div className={cx('content')}>
        <div className={cx('name-line')}>
          {selectionTag}
          <Tooltip title={props.children} zIndex={3000} getPopupContainer={() => document.body}>
            <span
              className={cx('name-text')}
              style={is_low_efficiency ? { color: 'var(--Text---text-color-3, #878A99)' } : undefined}
            >
              {props.children}
            </span>
          </Tooltip>
          {nameWithTags}
        </div>
      {showRiskTags && <AlertAllTagList {...alterTagProps} supplier={supplier}/>}
      </div>
      {onDelete && <div className={cx('supplier-delete')}>
        <Button
          size="xSmall"
          color="grey"
          type="text-subtle"
          onClick={(e) => {
            onDelete(supplier, e)
          }}
          icon={<Close size={'12px'} className={cx('delete-icon')} />}
        />
      </div>}
    </div>
  }


  return <span className={cx(
      'supplier-name', wrapperLayout,
      className, warningClassName)}
               {...restProps}>
    <div className={cx('name-line')}>
      {selectionTag}
      <Tooltip title={props.children} zIndex={3000} getPopupContainer={() => document.body}>
        <span
          className={cx('name-text')}
          style={is_low_efficiency ? { color: 'var(--Text---text-color-3, #878A99)' } : undefined}
        >
          {props.children}
        </span>
      </Tooltip>
      {nameWithTags}
    </div>

    {showRiskTags && <AlertAllTagList {...alterTagProps} supplier={supplier}/>}
  </span>
}


type SupplierNameListProps = {
  nameKey?: string,
  suppliers: any,
  listWrapperProps?: React.HTMLAttributes<HTMLDivElement>
  split?: React.ReactNode,
  ellipsis?: boolean,
  ellipsisNum?: number,
  isExclusiveSupplierList?: number[]
  isLowestPriceLast6mList?: number[]
  warningMap?: Record<number, boolean>;
} & Omit<SupplierNameProps, 'supplier' | 'warning'> & React.HTMLAttributes<HTMLSpanElement>

export function SupplierNameList(props: SupplierNameListProps) {
  const { nameKey, suppliers, listWrapperProps, split = '', ellipsis = true, ellipsisNum = 6, isExclusiveSupplierList, isLowestPriceLast6mList, warningMap, ...rest } = props
  const { className: listWrapperClassName, ...listWrapperRestProps } = listWrapperProps || {};
  const [expand, setExpand] = useState(true);

  const showSuppliers = useMemo(() => {
    if (!ellipsis) {
      return suppliers;
    }
    return expand ? suppliers : suppliers?.slice(0, ellipsisNum);
  }, [suppliers, ellipsisNum, ellipsis, expand]);

  if (!suppliers?.length) {
    return <>--</>
  }

  return <div>
    <div className={cx('supplier-name-list', listWrapperClassName)} {...listWrapperRestProps}>
      {showSuppliers?.map((supplier, index) => {
        return <div className={cx('list-item')} key={index}>
          <SupplierName
            key={index}
            wrapperLayout={'card'}
            supplier={supplier}
            showIsExclusive={isExclusiveSupplierList?.includes(supplier?.supplier_id)}
            showIsLowestPriceLast6m={isLowestPriceLast6mList?.includes(supplier?.supplier_id)}
            warning={warningMap?.[supplier?.supplier_id]}
            {...rest}>
            {supplier?.[nameKey] || supplier?.['supplier_full_name'] || supplier?.['full_name_with_service_info']}
          </SupplierName>
          {index !== showSuppliers?.length - 1 && <span className={cx('split-node')}>{split}</span>}
        </div>
      })}
    </div>
    {ellipsis && !!suppliers?.length && suppliers.length > ellipsisNum && <Button 
      type='link' 
      onClick={() => setExpand(!expand)}
      className='m-t-4'
    >
      {expand ? '收起' : `展开剩余 ${suppliers.length - ellipsisNum} 项`}
    </Button>}
  </div>
}
